import { Card } from "@/components/ui/card";
import {
  Chart,
  DateRangePicker,
  DownloadCSVButton,
  FilterByChips,
  GroupBySelect,
  REPORT_EXPENSES_SPEND_TABLE_COLUMN_OPTIONS,
  ReportExpensesSpendTable,
  ReportExpensesSpendTableColumnKeys,
} from "../components";
import { FilterCategoryEnum } from "@common/utils/graphql";
import {
  SelectFilterOptions,
  useFilterOptions,
} from "@common/components/SelectFilterOptions";
import { useReportsTabInputs } from "./useReportsTabInputs";
import { useEffect } from "react";
import { Separator } from "@/components/ui/separator";
import { Body } from "@hypercard/web-components";
import {
  CheckSquare,
  DownloadSimple,
  LockSimple,
  Square,
  Table,
} from "@phosphor-icons/react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

export const ReportsTab = () => {
  const {
    dateRange,
    groupedBy,
    groupByCustomFieldEntityId,
    filterOptions,
    spendTableColumns,
    upsert,
    reset,
  } = useReportsTabInputs();
  const {
    value: filterOptionsValue,
    query: chartQuery,
    onSelect: onSelectFilterOption,
  } = useFilterOptions(filterOptions);

  // Update filterOptions when filterOptionsValue changes
  useEffect(() => {
    upsert({ filterOptions: filterOptionsValue });
  }, [upsert, filterOptionsValue]);

  // TODO: consolidate with useChartQuery
  const endDateStr =
    dateRange.endDate.toISOString().split("T")[0] + "T23:59:59.999Z";
  const startDateStr =
    dateRange.startDate.toISOString().split("T")[0] + "T00:00:00.000Z";

  return (
    <Card className="space-y-6 p-6">
      {/* Inputs */}
      <div className="flex items-end space-x-4">
        <SelectFilterOptions
          value={filterOptions}
          onSelect={onSelectFilterOption}
          categories={[
            FilterCategoryEnum.ExpenseCategory,
            FilterCategoryEnum.Department,
            FilterCategoryEnum.Employee,
            FilterCategoryEnum.Merchant,
            FilterCategoryEnum.PolicyStatus,
            FilterCategoryEnum.ReportStatus,
            FilterCategoryEnum.ExpenseStatus,
            FilterCategoryEnum.Reviewer,
            FilterCategoryEnum.CustomField,
          ]}
          className="min-w-[250px]"
        />
        <GroupBySelect
          queryType="report"
          value={{ groupedBy, groupByCustomFieldEntityId }}
          onChange={({ groupedBy, groupByCustomFieldEntityId }) =>
            upsert({ groupedBy, groupByCustomFieldEntityId })
          }
        />
        <DateRangePicker
          dateRange={dateRange}
          onChange={(dateRange) => upsert({ dateRange })}
        />
        <div className="flex-1" />
        <button className="flex h-10 items-center" onClick={reset}>
          <Body
            size="sm"
            weight="medium"
            className="text-white/70 underline transition-colors hover:text-white"
          >
            Reset
          </Body>
        </button>
      </div>
      <FilterByChips
        filterBy={filterOptions}
        onRemoveFilter={onSelectFilterOption}
      />

      {/* Chart */}
      <Chart
        query={{
          queryType: "report",
          startDate: dateRange.startDate,
          endDate: dateRange.endDate,
          departmentId: chartQuery.departmentId,
          employeeId: chartQuery.employeeId,
          merchantName: chartQuery.merchantName,
          reviewerEmployeeId: chartQuery.reviewerEmployeeId,
          reportProps: {
            groupedBy,
            customField: chartQuery.customField,
            expenseCategoryGLId: chartQuery.expenseCategoryGLId,
            expenseReportStatus: chartQuery.expenseReportStatus,
            expenseStatus: chartQuery.expenseStatus,
            policyStatus: chartQuery.policyStatus,
            groupByCustomFieldEntityId,
          },
          // TODO: add payment method
        }}
        className="h-[400px]"
      />

      {/* Spend Table Section */}
      <div>
        <Separator />

        {/* Spend Table Inputs */}
        <div className="mb-4 mt-4 flex items-center space-x-2 pl-3 pr-1">
          <Body size="md" weight="regular" className="text-right">
            Spend Data
          </Body>
          <div className="flex-1" />
          <DropdownMenu>
            <DropdownMenuTrigger className="flex h-8 w-8 items-center justify-center rounded transition-colors hover:bg-white/10">
              <Table weight="bold" className="size-4" />
            </DropdownMenuTrigger>
            {/* TODO: make these options draggable to change order */}
            <DropdownMenuContent className="flex min-w-[200px] flex-col p-0">
              <div className="p-1">
                <div className="flex w-full cursor-not-allowed items-center space-x-2 p-2">
                  <CheckSquare weight="fill" className="size-4 text-white/50" />
                  <Body size="sm" weight="medium" className="text-white/70">
                    {ReportExpensesSpendTableColumnKeys.Merchant}
                  </Body>
                  <div className="flex-1" />
                  <LockSimple weight="bold" className="size-4 text-white/50" />
                </div>
              </div>
              <Separator />
              <div className="p-1">
                {REPORT_EXPENSES_SPEND_TABLE_COLUMN_OPTIONS.map((option) => {
                  const isSelected = spendTableColumns.includes(option);
                  return (
                    <button
                      key={option}
                      className="flex w-full items-center space-x-2 rounded-sm p-2 hover:bg-white/10"
                      onClick={() => {
                        if (isSelected) {
                          upsert({
                            spendTableColumns: spendTableColumns.filter(
                              (column) => column !== option,
                            ),
                          });
                        } else {
                          upsert({
                            spendTableColumns: [...spendTableColumns, option],
                          });
                        }
                      }}
                    >
                      {isSelected ? (
                        <CheckSquare weight="fill" className="size-4" />
                      ) : (
                        <Square className="size-4" />
                      )}
                      <Body size="sm" weight="medium">
                        {option}
                      </Body>
                    </button>
                  );
                })}
              </div>
              <Separator />
              <div className="p-1">
                <div className="flex w-full cursor-not-allowed items-center space-x-2 p-2">
                  <CheckSquare weight="fill" className="size-4 text-white/50" />
                  <Body size="sm" weight="medium" className="text-white/70">
                    {ReportExpensesSpendTableColumnKeys.Amount}
                  </Body>
                  <div className="flex-1" />
                  <LockSimple weight="bold" className="size-4 text-white/50" />
                </div>
              </div>
            </DropdownMenuContent>
          </DropdownMenu>
          <DownloadCSVButton
            filters={{
              createdAfter: dateRange.startDate,
              createdBefore: dateRange.endDate,
              employeeId: chartQuery.employeeId,
              expenseReportStatus: chartQuery.expenseReportStatus,
              // TODO: add all fields here
            }}
          >
            <div className="flex h-8 w-8 items-center justify-center rounded transition-colors hover:bg-white/10">
              <DownloadSimple weight="bold" className="size-4" />
            </div>
          </DownloadCSVButton>
        </div>

        {/* Table */}
        <ReportExpensesSpendTable
          filters={{
            transactedBefore: endDateStr,
            transactedAfter: startDateStr,
            departmentId: chartQuery.departmentId,
            employeeId: chartQuery.employeeId,
            expenseCategoryGLId: chartQuery.expenseCategoryGLId,
            expenseReportStatus: chartQuery.expenseReportStatus,
            policyStatus: chartQuery.policyStatus,
            reviewerEmployeeId: chartQuery.reviewerEmployeeId,
            expenseStatus: chartQuery.expenseStatus,
            customField: chartQuery.customField,
            // TODO: add payment method
            // TODO: add merchant name
          }}
          selectedColumns={spendTableColumns}
        />
      </div>
    </Card>
  );
};
